import React, { useCallback, useMemo, useState } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { ChartCard } from "../components/dashboard/ChartCard";
import { FormCard } from "../components/dashboard/FormCard";
import { TableCard } from "../components/dashboard/TableCard";
import { DemoBadge } from "../components/DemoBadge";
import { PreviewBanner } from "../components/PreviewBanner";
import { buttonClassName } from "../components/ui/Button";
import { latestSuccessAttempt, rankedCollections } from "../lib/after-auth";
import { cardGridClass, cardSpanClass } from "../lib/card-grid";
import { chartRowStatus } from "../lib/chart-row-status";
import { DashboardDto, PanelDto } from "../lib/generated/api-client";
import { HIDDEN_RECORD_MESSAGE } from "../lib/member-access";
import { supersededByCollection } from "../lib/nav-dashboards";
import {
  hrefWithPreview,
  PREVIEW_PARAM,
  previewIdFromLocation,
  rememberedPreviewName,
} from "../lib/preview-as";
import { usePreviewSession } from "../lib/preview-session";
import { Field } from "../lib/schema-field";
import { useDatasources } from "../lib/useDatasources";

const GenerateDashboardLink: React.FC = () => (
  <Link
    to="/data-sources"
    className={buttonClassName("primary", "sm", "px-3")}
  >
    Generate pages
  </Link>
);

const Dashboard: React.FC = () => {
  const { datasourceId, id: dashboardId } = useParams<{
    datasourceId: string;
    id: string;
  }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewMemberId = previewIdFromLocation(location);
  const preview = usePreviewSession();
  const { datasources } = useDatasources();
  const datasource = datasources.find((ds) => ds.id === datasourceId);
  const demoMode = !!(datasource as any)?.isDemo;
  // A pasted dashboard URL is still this panel. Writes stay off, and the
  // redirect back to a collection keeps the preview id.
  const readOnly = !!(datasource as any)?.readOnly || demoMode || !!previewMemberId;
  const previewHere =
    !!previewMemberId &&
    preview.datasourceId === datasourceId &&
    preview.memberId === previewMemberId;
  const previewName =
    (previewMemberId && rememberedPreviewName(previewMemberId)) ||
    preview.view?.email ||
    "this member";
  const exitPreview = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(PREVIEW_PARAM);
    setSearchParams(next);
  };
  const previewBanner = previewMemberId ? (
    <PreviewBanner
      name={previewName}
      message={previewHere ? preview.error : null}
      onExit={exitPreview}
    />
  ) : null;

  const attempt = latestSuccessAttempt(datasource?.analyzeAttempts);

  // Dashboards live on the SUCCESS analyse (same path Nav uses). Looking at
  // datasource.uiGenerationAttempts is always empty. That field is on
  // AnalyzeAttemptDto, not DatasourceDto.
  const dashboard = useMemo<DashboardDto | undefined>(
    () =>
      (attempt?.uiGenerationAttempts ?? [])
        .flatMap((generation) => generation?.panels ?? [])
        .flatMap((panel: PanelDto) => panel?.dashboards ?? [])
        .find((entry: DashboardDto) => entry.id === dashboardId),
    [attempt, dashboardId]
  );

  /** Column definitions for a collection, from the analyse attempt. */
  const schemaFor = useMemo(() => {
    const byName = new Map<string, Field[]>();
    (attempt?.databaseCollections ?? []).forEach((collection: any) => {
      byName.set(collection.name, (collection.fields ?? []) as Field[]);
    });
    return (name?: string) => (name ? byName.get(name) ?? [] : []);
  }, [attempt]);

  // Which charts came back with nothing to draw. A card only learns this after
  // its own series query resolves, so it cannot be filtered before render: the
  // cards report back and the row re-lays out around the survivors. Declared
  // above the early returns because hook order cannot depend on whether the
  // dashboard was found.
  const [unplottable, setUnplottable] = useState<Record<string, boolean>>({});

  const reportUnplottable = useCallback(
    (id: string) => (isUnplottable: boolean) =>
      setUnplottable((current) =>
        current[id] === isUnplottable
          ? current
          : { ...current, [id]: isUnplottable },
      ),
    [],
  );

  if (!datasource) {
    return (
      <div className="p-6 text-sm text-od-muted">
        That datasource is not in your workspace.
      </div>
    );
  }

  if (previewMemberId && previewHere && preview.error) {
    return <div className="p-3">{previewBanner}</div>;
  }

  if (!dashboard) {
    return (
      <div className="min-h-full bg-od-bg p-4">
        {previewBanner}
        <h1 className="m-0 mb-4 text-lg font-semibold text-od-text">
          Page
        </h1>
        <p className="m-0 mb-4 text-sm text-od-muted">
          This page no longer exists. Generate pages from Data sources.
        </p>
        {!previewMemberId && <GenerateDashboardLink />}
      </div>
    );
  }

  const { dataTables = [], charts = [], forms = [] } = dashboard;

  // The collection route owns the entity. It draws the same charts over the
  // same grid, so a single-entity dashboard is a duplicate room and the rail
  // stopped linking it in f742b59. A pasted URL would otherwise still land
  // here, on a page nothing references. `replace`, so Back returns to wherever
  // the visitor came from rather than bouncing through the redirect.
  //
  // Multi-entity pages and pages naming a collection the analyse did not
  // return have no equivalent route, so they still render. Same predicate the
  // rail filters on, so the two cannot disagree about which pages exist.
  const superseded = supersededByCollection(
    dashboard,
    (attempt?.databaseCollections ?? []).map(
      (collection: any) => collection.name as string,
    ),
  );
  if (superseded) {
    return (
      <Navigate
        to={hrefWithPreview(
          `/datasources/${datasourceId}/collections/${superseded}`,
          previewMemberId,
        )}
        replace
      />
    );
  }

  const previewTabs =
    previewHere && preview.view
      ? new Set(preview.view.tabs.map((tab) => tab.tab))
      : null;
  const previewLoading = !!previewMemberId && !preview.error && !preview.view;
  const allowedTab = (name?: string | null): boolean => {
    if (!previewMemberId) return true;
    if (!previewTabs) return false;
    return !!name && previewTabs.has(name);
  };

  if (previewLoading) {
    return (
      <div className="p-3">
        {previewBanner}
        <p className="m-0 text-sm text-od-muted">Loading…</p>
      </div>
    );
  }

  const visibleForms = (readOnly ? [] : forms).filter((form) =>
    allowedTab(form.fields?.[0]?.collection),
  );
  // Guest already ranks collections so the demo does not open on a join table.
  // Overview used information_schema order, so the first card was
  // task_dependencies. Reuse the same ranker for card order.
  const rank = new Map(
    rankedCollections(attempt?.databaseCollections).map((collection, index) => [
      collection.name as string,
      index,
    ]),
  );
  const orderedTables = [...dataTables]
    .sort((a, b) => {
      const left = rank.get(a.collection) ?? Number.MAX_SAFE_INTEGER;
      const right = rank.get(b.collection) ?? Number.MAX_SAFE_INTEGER;
      return left - right;
    })
    .filter((table) => allowedTab(table.collection));
  const shownCharts = charts.filter((chart) =>
    allowedTab(chart.collections?.[0]),
  );
  const isEmpty =
    !orderedTables.length && !shownCharts.length && !visibleForms.length;

  if (previewMemberId && isEmpty) {
    return (
      <div className="flex flex-col gap-2.5 p-3">
        {previewBanner}
        <p className="m-0 text-sm text-od-muted" data-testid="hidden-tab">
          {HIDDEN_RECORD_MESSAGE}
        </p>
      </div>
    );
  }

  // API 8674e9e emits one page per entity. That page is the collection, not
  // an Overview of cards, so a 420px TableCard plus a duplicate dashboard
  // heading is leftover chrome. Combined-entity pages (several tables, or
  // charts/forms beside a table) still use the card grid.
  // visibleForms, not forms: a read-only source hides forms, so a lone table
  // there is still a one-entity page.
  // The chart/form row is a 2-up grid, so an odd card count would leave the
  // final tile empty. `cardSpanClass` hands that card both columns instead: a
  // wide figure is a real chart, an empty half-row is a missing one. Shared
  // with the collection landing route so both grids obey one rule.
  // Counted over plottable charts, not all charts: a card whose series has
  // nothing to draw renders null, so counting it sized the row for a cell that
  // never appears and put the hole back that the span rule exists to remove.
  const plottableCharts = shownCharts.filter((chart) => !unplottable[chart.id]);
  const chartStatus = chartRowStatus({ declared: shownCharts, unplottable });
  const cardCount = plottableCharts.length + visibleForms.length;
  const spanClass = (index: number) => cardSpanClass(index, cardCount);

  const entityPage =
    orderedTables.length === 1 &&
    shownCharts.length === 0 &&
    visibleForms.length === 0;

  if (entityPage) {
    const table = orderedTables[0];
    const collection = attempt?.databaseCollections?.find(
      (entry) => entry.name === table.collection,
    );
    const schema = schemaFor(table.collection);
    return (
      <div className="flex h-full min-h-0 flex-col bg-od-bg">
        {previewBanner}
        <TableCard
          key={table.id}
          variant="page"
          datasourceId={datasourceId!}
          name={table.name}
          collection={table.collection}
          schema={schema}
          indexes={collection?.indexes}
          primaryFieldName={schema.find((field) => field.primary)?.name}
          demoMode={demoMode}
          readOnly={readOnly}
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-od-bg p-4">
      {previewBanner}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <h1 className="m-0 text-lg font-semibold text-od-text">
          {dashboard.name}
        </h1>
        {demoMode && <DemoBadge />}
      </div>

      {isEmpty && (
        <div className="mb-4">
          <p className="m-0 mb-4 text-sm text-od-muted">
            This page is empty. Generate pages from Data sources.
          </p>
          {!previewMemberId && <GenerateDashboardLink />}
        </div>
      )}

      {/* Records before charts. `#/guest` lands on this Overview, and at
          393 the first table heading sat at y=1654 under four figures, so
          the fold was a chart the colleague cannot edit. Collection already
          draws the grid first; this page is the same promise. */}
      <div className="mb-4 grid grid-cols-1 gap-4">
        {orderedTables.map((table) => (
          <TableCard
            key={table.id}
            datasourceId={datasourceId!}
            name={table.name}
            collection={table.collection}
            schema={schemaFor(table.collection)}
            demoMode={demoMode}
            readOnly={readOnly}
          />
        ))}
      </div>

      {(shownCharts.length > 0 || visibleForms.length > 0) && (
        // Two columns only when there is a second card to fill the row, and
        // the last card spans both when the count is odd. An unconditional
        // lg:grid-cols-2 left a blank tile beside the final card — one card
        // was an L-shaped void, three left an empty fourth cell — which reads
        // as a chart that failed to load rather than as a layout.
        <div
          className={`mb-4 ${cardGridClass(cardCount)}`}
        >
          {shownCharts.map((chart) => {
            // -1 while the card is mounted but reporting unplottable. It draws
            // nothing, so it has no cell to place.
            const plottableIndex = plottableCharts.findIndex(
              (plottable) => plottable.id === chart.id,
            );

            return (
              <ChartCard
                key={chart.id}
                className={plottableIndex === -1 ? "" : spanClass(plottableIndex)}
                onUnplottable={reportUnplottable(chart.id)}
                datasourceId={datasourceId!}
                name={chart.name}
                title={chart.title}
                description={chart.description}
                type={chart.type}
                groupBy={(chart as any).groupBy}
                bucket={(chart as any).bucket}
                measure={(chart as any).measure}
                collection={chart.collections?.[0]}
                schema={schemaFor(chart.collections?.[0])}
              />
            );
          })}
          {visibleForms.map((form, index) => (
            <FormCard
              key={form.id}
              className={spanClass(plottableCharts.length + index)}
              datasourceId={datasourceId!}
              name={form.name}
              title={form.title}
              description={form.description}
              fields={form.fields ?? []}
              schema={schemaFor(form.fields?.[0]?.collection)}
              demoMode={demoMode}
            />
          ))}
        </div>
      )}

      {/* Same promise the collection route makes: a chart the generator
          declared but could not draw is named rather than dropped. Only
          multi-entity pages reach this component now (single-entity ones
          redirect), and those are exactly the pages where a missing figure is
          hardest to notice. */}
      {chartStatus && (
        <p className="mt-4 text-sm text-od-muted">{chartStatus}</p>
      )}
    </div>
  );
};

export default Dashboard;
